var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Big from "big.js";
import BN from "bn.js";
import { Bitcoin, ExchangeRate, InterBtc, Interlay, KBtc, Kintsugi, Kusama, MonetaryAmount, Polkadot, VoteInterlay, VoteKintsugi, } from "@interlay/monetary-js";
import { u32 } from "@polkadot/types";
import { decodeBytesAsString, newForeignAssetId, newCurrencyId, storageKeyToNthInner } from "./encoding";
import { DefaultAssetRegistryAPI } from "../parachain/asset-registry";
import { DefaultLoansAPI } from "../parachain";
import { DefaultAMMAPI } from "../parachain/amm";
// set maximum exponents
Big.PE = 21;
Big.NE = -12;
export const ATOMIC_UNIT = 0;
export function computeLazyDistribution(stake, perToken, tally) {
    return stake.mul(perToken).sub(tally);
}
export function atomicToBaseAmount(atomicAmount, currency) {
    return new Big(atomicAmount).div(new Big(10).pow(currency.decimals));
}
export function newMonetaryAmount(amount, currency, base = false) {
    const finalAmount = base ? new Big(amount) : atomicToBaseAmount(amount, currency);
    return new MonetaryAmount(currency, finalAmount);
}
export function newCollateralBTCExchangeRate(rate, counterCurrency, useBaseUnits = false) {
    const [baseCurrencyUnit, counterCurrencyUnit] = useBaseUnits
        ? [Bitcoin.decimals, counterCurrency.decimals]
        : [ATOMIC_UNIT, ATOMIC_UNIT];
    return new ExchangeRate(Bitcoin, counterCurrency, rate, baseCurrencyUnit, counterCurrencyUnit);
}
export function createFeeEstimationOracleKey(api) {
    return api.createType("InterbtcPrimitivesOracleKey", { FeeEstimation: null });
}
export function createExchangeRateOracleKey(api, collateralCurrency) {
    const currencyId = newCurrencyId(api, collateralCurrency);
    return api.createType("InterbtcPrimitivesOracleKey", { ExchangeRate: currencyId });
}
export function toVoting(governanceCurrency) {
    switch (governanceCurrency) {
        case Interlay:
            return VoteInterlay;
        case Kintsugi:
            return VoteKintsugi;
        default:
            throw new Error("Provided currency is not a governance currency");
    }
}
/**
 * Get all collateral currencies (tokens as well as foreign assets).
 *
 * Will return all collateral currencies for which the parachain has a system collateral ceiling value
 * greater than zero.
 * @param api ApiPromise instance to query the parachain
 * @returns An array of collateral currencies.
 */
export function getCollateralCurrencies(api) {
    return __awaiter(this, void 0, void 0, function* () {
        const collatCeilEntries = yield api.query.vaultRegistry.systemCollateralCeiling.entries();
        const isOptionGreaterThanZero = (value) => value.isNone ? false : value.unwrap().toBn().gt(new BN(0));
        const collateralCurrencyPrimitives = collatCeilEntries
            .filter(([_, ceiling]) => isOptionGreaterThanZero(ceiling))
            .map(([storageKey, _]) => storageKeyToNthInner(storageKey));
        return Promise.all(collateralCurrencyPrimitives.map((currencyPair) => currencyIdToMonetaryCurrency(api, currencyPair.collateral)));
    });
}
export function isForeignAsset(currencyExt) {
    // disable rule, use of any is deliberate for run time check
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return currencyExt.foreignAsset !== undefined;
}
export function isLendToken(currencyExt) {
    // disable rule, use of any is deliberate for run time check
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return currencyExt.lendToken !== undefined;
}
export function isStandardLpToken(currencyExt) {
    return currencyExt.lpToken !== undefined;
}
export function isStableLpToken(currencyExt) {
    return currencyExt.stableLpToken !== undefined;
}
export function isCurrency(currencyExt) {
    return (!isForeignAsset(currencyExt) &&
        !isLendToken(currencyExt) &&
        !isStandardLpToken(currencyExt) &&
        !isStableLpToken(currencyExt));
}
export function isCurrencyEqual(currency, otherCurrency) {
    if (isCurrency(currency) && isCurrency(otherCurrency)) {
        return currency.ticker === otherCurrency.ticker;
    }
    else if (isForeignAsset(currency) && isForeignAsset(otherCurrency)) {
        return currency.foreignAsset.id === otherCurrency.foreignAsset.id;
    }
    else if (isLendToken(currency) && isLendToken(otherCurrency)) {
        return currency.lendToken.id === otherCurrency.lendToken.id;
    }
    else if (isStandardLpToken(currency) && isStandardLpToken(otherCurrency)) {
        return (isCurrencyEqual(currency.lpToken.token0, otherCurrency.lpToken.token0) &&
            isCurrencyEqual(currency.lpToken.token1, otherCurrency.lpToken.token1));
    }
    else if (isStableLpToken(currency) && isStableLpToken(otherCurrency)) {
        return currency.stableLpToken.poolId === otherCurrency.stableLpToken.poolId;
    }
    return false;
}
export function getCurrencyIdentifier(currency) {
    if (isForeignAsset(currency)) {
        return { foreignAsset: currency.foreignAsset.id };
    }
    if (isLendToken(currency)) {
        return { lendToken: currency.lendToken.id };
    }
    if (isStableLpToken(currency)) {
        return { stableLpToken: currency.stableLpToken.poolId };
    }
    if (isStandardLpToken(currency)) {
        const token0 = getCurrencyIdentifier(currency.lpToken.token0);
        const token1 = getCurrencyIdentifier(currency.lpToken.token1);
        return { lpToken: [token0, token1] };
    }
    return { token: currency.ticker };
}
export function currencyIdToMonetaryCurrency(api, currencyId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (currencyId.isToken) {
            return tokenSymbolToCurrency(currencyId.asToken);
        }
        else if (currencyId.isForeignAsset) {
            const foreignAssetId = currencyId.asForeignAsset;
            return getForeignAssetFromId(api, foreignAssetId);
        }
        else if (currencyId.isLendToken) {
            const underlyingCurrency = yield getUnderlyingCurrencyFromLendTokenId(api, currencyId);
            return DefaultLoansAPI.getLendTokenFromUnderlyingCurrency(underlyingCurrency, currencyId);
        }
        else if (currencyId.isLpToken) {
            return getStandardLpTokenFromCurrencyId(api, currencyId);
        }
        else if (currencyId.isStableLpToken) {
            return getStableLpTokenFromCurrencyId(api, currencyId);
        }
        throw new Error(`No handling implemented for currencyId type of ${currencyId.type}`);
    });
}
/**
 * A method that will only try to find a hard-coded currencies.
 * Only for use when we are certain the currency is not a foreign asset.
 * @param tokenSymbol the InterbtcPrimitivesTokenSymbol to look up
 */
export function tokenSymbolToCurrency(tokenSymbol) {
    if (tokenSymbol.isIbtc) {
        return InterBtc;
    }
    else if (tokenSymbol.isDot) {
        return Polkadot;
    }
    else if (tokenSymbol.isKsm) {
        return Kusama;
    }
    else if (tokenSymbol.isKbtc) {
        return KBtc;
    }
    else if (tokenSymbol.isKint) {
        return Kintsugi;
    }
    else if (tokenSymbol.isIntr) {
        return Interlay;
    }
    throw new Error(`No entry provided for token symbol of type '${tokenSymbol === null || tokenSymbol === void 0 ? void 0 : tokenSymbol.type}'`);
}
/**
 * Get foreign asset by its id.
 * @param id The id of the foreign asset.
 * @returns The foreign asset.
 */
export function getForeignAssetFromId(api, id) {
    return __awaiter(this, void 0, void 0, function* () {
        const u32Id = id instanceof u32 ? id : newForeignAssetId(api, id);
        const optionMetadata = yield api.query.assetRegistry.metadata(u32Id);
        if (!optionMetadata.isSome) {
            return Promise.reject(new Error("Foreign asset not found"));
        }
        const currencyPart = DefaultAssetRegistryAPI.metadataToCurrency(optionMetadata.unwrap());
        const coingeckoId = decodeBytesAsString(optionMetadata.unwrap().additional.coingeckoId);
        const numberId = id instanceof u32 ? id.toNumber() : id;
        return Object.assign({ foreignAsset: {
                id: numberId,
                coingeckoId,
            } }, currencyPart);
    });
}
/**
 * Get underlying currency of lend token id,
 *
 * @param lendTokenId Currency id of the lend token to get currency from
 * @returns Underlying CurrencyExt for provided lend token
 */
export function getUnderlyingCurrencyFromLendTokenId(api, lendTokenId) {
    return __awaiter(this, void 0, void 0, function* () {
        const underlyingCurrencyId = yield api.query.loans.underlyingAssetId(lendTokenId);
        const underlyingCurrency = yield currencyIdToMonetaryCurrency(api, underlyingCurrencyId.unwrap());
        return underlyingCurrency;
    });
}
/**
 * Get standard LP token currency lib type from currencyId primitive.
 *
 * @param currencyId Id of standard LP token.
 * @returns {StandardLpToken} Lib type currency object for standard LP token.
 */
export function getStandardLpTokenFromCurrencyId(api, currencyId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!currencyId.isLpToken) {
            throw new Error("Provided currencyId is not standard LP token.");
        }
        const standardLpTokenCurrencyId = currencyId.asLpToken;
        const [token0, token1] = yield Promise.all(standardLpTokenCurrencyId.map((currencyId) => currencyIdToMonetaryCurrency(api, currencyId)));
        return {
            name: `LP ${token0.ticker}-${token1.ticker}`,
            ticker: `LP ${token0.ticker}-${token1.ticker}`,
            decimals: 18,
            lpToken: {
                token0,
                token1,
            },
        };
    });
}
/**
 * Get stable LP token currency lib type from currencyId primitive.
 *
 * @param currencyId Id of stable LP token.
 * @returns {StableLpToken} Lib type currency object for stable LP token.
 */
export function getStableLpTokenFromCurrencyId(api, currencyId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!currencyId.isStableLpToken) {
            throw new Error("Provided currencyId is not stable LP token.");
        }
        const poolId = currencyId.asStableLpToken.toNumber();
        const poolData = yield api.query.dexStable.pools(poolId);
        if (!poolData.isSome) {
            throw new Error(`getStableLpToken: Invalid pool data for currencyId ${currencyId.toString()}`);
        }
        const basePoolData = DefaultAMMAPI.getStablePoolInfo(poolData.unwrap());
        if (basePoolData === null) {
            throw new Error("Provided currencyId is not active LP token.");
        }
        return DefaultAMMAPI.getStableLpTokenFromPoolData(poolId, basePoolData);
    });
}
//# sourceMappingURL=currency.js.map